import type { DocNode } from '@atlaskit/adf-schema/doc';
import { type PluginInvocationMessage } from '@atlassian/conversation-assistant-service-api/plugins';
import {
	type AgentResponse,
	type AssistantMessage,
	type HumanMessage,
	type HumanMessageDisplayContext,
	isAgentResponse,
	type MessageFileId,
	type StreamActionComplete,
	type StreamActionPart,
	type StreamActionPrepare,
	type StreamAnswerPart,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { isRecord } from '@atlassian/conversation-assistant-utils/isRecord';

type StreamActionUpdate = StreamActionPart | StreamActionPrepare | StreamActionComplete;

// ****************************************************************************
// Getters and Setters
// ****************************************************************************

export const getId = (streamResponse: StreamResponse): string => streamResponse.message.message.id;

const addAnswerPartAssistantMessage = (
	response: AssistantMessage,
	answerPart: StreamAnswerPart,
): AssistantMessage => {
	const content = `${response.content}${answerPart.message.content}`;

	// Remove source indexes, e.g [^1^] from the content
	const cleanedContent = content.replace(/\[\^\d+\^\]/g, '');

	return {
		...response,
		content: cleanedContent,
	};
};

const addAnswerPartAgentResponse = (
	response: AgentResponse,
	answerPart: StreamAnswerPart,
): AgentResponse => ({
	...response,
	message: addAnswerPartAssistantMessage(response.message, answerPart),
});

// ****************************************************************************
// Action streaming accumulation configuration
// ****************************************************************************

const toPathArr = (path: string | string[]): string[] =>
	Array.isArray(path) ? path : path.split('.').filter(Boolean);

const getAtPath = (obj: unknown, path: string[]): unknown => {
	let cur: unknown = obj;
	for (const key of path) {
		if (!isRecord(cur)) {
			return undefined;
		}
		cur = cur[key];
	}
	return cur;
};

const setAtPath = (
	obj: unknown,
	path: string[],
	value: string,
): string | Record<string, unknown> => {
	if (path.length === 0) {
		return value;
	}
	const [head, ...rest] = path;
	const base: Record<string, unknown> = isRecord(obj) ? obj : {};
	return {
		...base,
		[head]: setAtPath(base[head], rest, value),
	};
};

// Defines which sub-field(s) of a message action's data should be accumulated by string appending.
// Keyed by action key. Values can be a dot path or an array path, or an array of multiple paths.
// E.g.:
// {
//   'create-document-action': 'content',
//   'update-document-action': ['content', 'meta.subtitle']
// }
let actionDataAccumulationConfig: Record<string, string | string[] | Array<string | string[]>> = {
	['aifc-confluence-page-create']: 'adf',
};

// Returns a new action based on previous and next, applying string-append accumulation
// for configured paths. Defaults to replacing fields from `next` when no rule applies.
export const getUpdatedAction = (prev: MessageAction, next: MessageAction): MessageAction => {
	const key = next.key;
	const rulePaths = actionDataAccumulationConfig[key];
	const prevData = prev?.data as unknown;
	const newData = next?.data as unknown;

	if (
		rulePaths &&
		typeof prevData === 'object' &&
		prevData !== null &&
		typeof newData === 'object' &&
		newData !== null
	) {
		const paths = Array.isArray(rulePaths) ? rulePaths : [rulePaths];
		let base: Record<string, unknown> = { ...(newData as Record<string, unknown>) };
		for (const p of paths) {
			const pathArr = toPathArr(p);
			const prevVal = getAtPath(prevData, pathArr);
			const nextVal = getAtPath(newData, pathArr);
			const accumulated = String(prevVal ?? '') + String(nextVal ?? '');
			const updated = setAtPath(base, pathArr, accumulated);
			// `setAtPath` only returns the raw `value` when the path is empty; here
			// `pathArr` is always non-empty (derived from a configured rule path), so
			// the result is always the spread record branch.
			if (isRecord(updated)) {
				base = updated;
			}
		}
		return { ...prev, data: base, status: next.status };
	}

	// Default: replace only data from next, keep other fields from prev
	return { ...prev, data: next.data, status: next.status };
};

export const updateAssistantMessageStreamActions = (
	actions: MessageAction[],
	actionPart: StreamActionUpdate,
	actionUpdaterOverride?: (prevAction: MessageAction, nextAction: MessageAction) => MessageAction,
): MessageAction[] => {
	const { key, invocationId } = actionPart.message;
	// Accumulate by `invocationId` when the part has one, so multiple same-`key` actions (e.g. several
	// page creates in a single message) stream independently; fall back to `key` for parts without an
	// invocationId. For single-action messages both resolve to the same action, so no experiment gate
	// is needed here.
	const existingActionIndex = invocationId
		? actions.findIndex((action) => action.invocationId === invocationId)
		: actions.findIndex((action) => action.key === key);

	if (existingActionIndex === -1) {
		return [...(actions || []), actionPart.message];
	}

	const updatedActions = [...actions];
	const prevAction = updatedActions[existingActionIndex];
	const nextAction = actionPart.message;
	updatedActions[existingActionIndex] = actionUpdaterOverride
		? actionUpdaterOverride(prevAction, nextAction)
		: getUpdatedAction(prevAction, nextAction);
	return updatedActions;
};

export const addAnswerPart = (
	response: StreamResponse,
	answerPart: StreamAnswerPart,
): StreamResponse => ({
	...response,
	message: addAnswerPartAgentResponse(response.message, answerPart),
});

export const addActionPart = (
	response: StreamResponse,
	actionPart: StreamActionUpdate,
	actionUpdaterOverride?: (prevAction: MessageAction, nextAction: MessageAction) => MessageAction,
): StreamResponse => {
	const { message } = response.message;
	const { actions } = message;
	const isActionsArrayPresent = actions && actions.length > 0;

	return {
		...response,
		message: {
			...response.message,
			message: {
				...message,
				actions: !isActionsArrayPresent
					? [actionPart.message]
					: updateAssistantMessageStreamActions(actions, actionPart, actionUpdaterOverride),
			},
		},
	};
};

const removeSensitiveDataAssistantMessage = (message: AssistantMessage): AssistantMessage => {
	const { plugin_invocations, ...rest } = message;
	return {
		...rest,
		plugin_invocations: plugin_invocations.map(
			(pluginInvocation): PluginInvocationMessage => ({
				...pluginInvocation,
				plugin_output: '--PRIVATE--',
			}),
		),
	};
};

const removeSensitiveDataAgentResponse = (message: AgentResponse): AgentResponse => ({
	...message,
	message: removeSensitiveDataAssistantMessage(message.message),
});

export const removeSensitiveData = (message: StreamResponse): StreamResponse => ({
	...message,
	message: removeSensitiveDataAgentResponse(message.message),
});

// ****************************************************************************
// Constructors
// ****************************************************************************
export const NO_PARENT_MESSAGE_ID = '00000000-0000-0000-0000-000000000000';

export const createHumanMessage = (
	id: string,
	content: string | DocNode,
	isFromSeededConversation?: boolean,
	requestId?: string,
	fileUploads?: MessageFileId[],
	displayContext?: HumanMessageDisplayContext,
): HumanMessage => {
	return {
		role: 'HUMAN',
		id,
		content,
		time_created: new Date().toISOString(),
		user_ari: 'unknown',
		plugin_invocations: [],
		isFromSeededConversation,
		requestId,
		file_uploads: fileUploads,
		...(displayContext ? { display_context: displayContext } : {}),
	};
};

export const toStreamResponse = (message: AssistantMessage | AgentResponse): StreamResponse => {
	if (isAgentResponse(message)) {
		return { type: 'FINAL_RESPONSE', message };
	}
	return { type: 'FINAL_RESPONSE', message: { message } };
};
